// ABOUTME: Cloudflare Workers auth provider using Better Auth cookie-based sessions
// ABOUTME: Implements IAuthProvider by calling Better Auth REST endpoints via fetch

import type {
  IAuthProvider,
  AuthResult,
  User,
  Session,
  AuthStateChangeCallback,
} from '../interfaces'

interface BetterAuthUser {
  id: string
  email: string
  name?: string
  emailVerified: boolean
  image?: string | null
  createdAt: string
  updatedAt: string
}

interface BetterAuthSession {
  id: string
  token: string
  expiresAt: string
  userId?: string
}

function mapUser(raw: BetterAuthUser): User {
  return {
    id: raw.id,
    email: raw.email,
    emailConfirmed: raw.emailVerified ?? false,
    metadata: { name: raw.name, image: raw.image },
    createdAt: new Date(raw.createdAt),
    updatedAt: new Date(raw.updatedAt),
  }
}

function mapSession(rawSession: BetterAuthSession, user: User): Session {
  return {
    accessToken: rawSession.token,
    expiresAt: new Date(rawSession.expiresAt),
    user,
  }
}

export class CloudflareAuthProvider implements IAuthProvider {
  private apiUrl: string
  private listeners: AuthStateChangeCallback[] = []
  private pollInterval: ReturnType<typeof setInterval> | null = null
  private lastSessionToken: string | null = null
  private checkSessionBound: (() => void) | null = null

  constructor(apiUrl: string) {
    this.apiUrl = apiUrl
  }

  // biome-ignore lint/suspicious/noExplicitAny: dynamic user metadata
  async signUp(email: string, password: string, metadata?: Record<string, any>): Promise<AuthResult> {
    try {
      const response = await fetch(`${this.apiUrl}/api/auth/sign-up/email`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password, name: metadata?.name || '' }),
      })

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}))
        return { user: null, session: null, error: new Error((errorData as { message?: string }).message || 'Sign up failed') }
      }

      const data = await response.json() as { user: BetterAuthUser; session: BetterAuthSession }
      const user = mapUser(data.user)
      const session = mapSession(data.session, user)

      this.lastSessionToken = data.session.token
      this.notifyListeners(session, user)

      return { user, session }
    } catch (err) {
      return { user: null, session: null, error: err instanceof Error ? err : new Error('Sign up failed') }
    }
  }

  async signIn(email: string, password: string): Promise<AuthResult> {
    try {
      const response = await fetch(`${this.apiUrl}/api/auth/sign-in/email`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password }),
      })

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}))
        return { user: null, session: null, error: new Error((errorData as { message?: string }).message || 'Sign in failed') }
      }

      const data = await response.json() as { user: BetterAuthUser; session: BetterAuthSession }
      const user = mapUser(data.user)
      const session = mapSession(data.session, user)

      this.lastSessionToken = data.session.token
      this.notifyListeners(session, user)

      return { user, session }
    } catch (err) {
      return { user: null, session: null, error: err instanceof Error ? err : new Error('Sign in failed') }
    }
  }

  async signOut(): Promise<void> {
    await fetch(`${this.apiUrl}/api/auth/sign-out`, {
      method: 'POST',
      credentials: 'include',
    })

    this.lastSessionToken = null
    this.notifyListeners(null, null)
  }

  async getSession(): Promise<Session | null> {
    try {
      const response = await fetch(`${this.apiUrl}/api/auth/session`, {
        method: 'GET',
        credentials: 'include',
      })

      if (!response.ok) {
        return null
      }

      const data = await response.json() as { session: BetterAuthSession; user: BetterAuthUser }
      if (!data.session || !data.user) {
        return null
      }

      const user = mapUser(data.user)
      return mapSession(data.session, user)
    } catch {
      return null
    }
  }

  async getUser(): Promise<User | null> {
    const session = await this.getSession()
    return session?.user ?? null
  }

  onAuthStateChange(callback: AuthStateChangeCallback): () => void {
    this.listeners.push(callback)
    this.startPolling()

    return () => {
      this.listeners = this.listeners.filter((l) => l !== callback)
      if (this.listeners.length === 0) {
        this.stopPolling()
      }
    }
  }

  async resetPassword(email: string): Promise<{ error?: Error }> {
    try {
      const response = await fetch(`${this.apiUrl}/api/auth/forgot-password`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      })

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}))
        return { error: new Error((errorData as { message?: string }).message || 'Password reset failed') }
      }

      return {}
    } catch (err) {
      return { error: err instanceof Error ? err : new Error('Password reset failed') }
    }
  }

  async updateUser(metadata: Record<string, unknown>): Promise<{ user: User | null; error?: Error }> {
    try {
      const response = await fetch(`${this.apiUrl}/api/auth/update-user`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(metadata),
      })

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}))
        return { user: null, error: new Error((errorData as { message?: string }).message || 'Update failed') }
      }

      const data = await response.json() as { user: BetterAuthUser }
      return { user: mapUser(data.user) }
    } catch (err) {
      return { user: null, error: err instanceof Error ? err : new Error('Update failed') }
    }
  }

  private startPolling(): void {
    if (this.pollInterval) return

    const checkSession = async () => {
      try {
        const session = await this.getSession()
        const currentToken = session?.accessToken ?? null

        if (currentToken !== this.lastSessionToken) {
          this.lastSessionToken = currentToken
          this.notifyListeners(session, session?.user ?? null)
        }
      } catch {
        // Polling failure is non-critical
      }
    }

    this.checkSessionBound = checkSession
    this.pollInterval = setInterval(checkSession, 30_000)

    if (typeof window !== 'undefined') {
      window.addEventListener('focus', checkSession)
    }
  }

  private stopPolling(): void {
    if (this.pollInterval) {
      clearInterval(this.pollInterval)
      this.pollInterval = null
    }
    if (this.checkSessionBound && typeof window !== 'undefined') {
      window.removeEventListener('focus', this.checkSessionBound)
      this.checkSessionBound = null
    }
  }

  private notifyListeners(session: Session | null, user: User | null): void {
    for (const listener of this.listeners) {
      listener(session, user)
    }
  }
}
